import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, ShieldAlert, Loader2, Play, Clock } from 'lucide-react';
import { api, apiError } from './api';

export default function Checkin() {
  const { token } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState('loading');
  const [err, setErr] = useState('');

  const load = () => api.get(`/officials/by-token/${token}`)
    .then((r) => { setInfo(r.data); setStatus((s) => (s === 'loading' ? (r.data.checked_in ? 'done' : 'ready') : s)); })
    .catch(() => setStatus((s) => (s === 'loading' ? 'error' : s)));
  useEffect(() => {
    load();
    const iv = setInterval(load, 20000); // auto-refresh so new knockout matches appear without reload
    return () => clearInterval(iv);
    /* eslint-disable-next-line */
  }, [token]);

  const checkin = async () => {
    setStatus('submitting');
    try {
      const { data } = await api.post('/officials/checkin', { token });
      setInfo((p) => ({ ...p, checked_in: true, checked_in_at: data.checked_in_at }));
      setStatus('done');
    } catch (e) { setErr(apiError(e)); setStatus('ready'); }
  };

  const Fixtures = () => {
    const fxs = info?.fixtures || [];
    if (fxs.length === 0) return <p className="text-center text-gray-600 text-sm mt-6">No matches assigned to you yet.</p>;
    return (
      <div className="w-full max-w-sm mt-8 space-y-3">
        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest text-center">Your Matches</p>
        {fxs.map((f) => (
          <div key={f.id} data-testid={`my-fixture-${f.id}`} className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-4">
            {(f.kickoff || f.pitch) && (
              <div className="flex items-center gap-3 mb-2 text-[10px] text-[#00BFFF] font-bold uppercase tracking-wider">
                {f.kickoff && <span>KO {f.kickoff}</span>}
                {f.pitch && <span>{f.pitch}</span>}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="font-bold text-sm flex-1 truncate">{f.home}</span>
              {f.status === 'completed'
                ? <span className="font-black bg-black px-3 py-1 rounded-lg text-sm">{f.home_score} - {f.away_score}</span>
                : <span className="text-gray-600 text-xs px-2">vs</span>}
              <span className="font-bold text-sm flex-1 truncate text-right">{f.away}</span>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-[10px] text-gray-500 flex items-center gap-1"><Clock size={11} /> {f.periods} × {f.period_length}min</span>
              {f.status === 'completed' ? (
                <span className="text-[11px] font-bold text-[#00D26A] uppercase tracking-wider">Played</span>
              ) : (
                <button data-testid={`start-match-${f.id}`} onClick={() => nav(`/ref/${token}/${f.id}`)}
                  className="bg-[#00BFFF] text-black font-bold text-xs px-4 py-2 rounded-full flex items-center gap-1.5">
                  <Play size={13} /> START MATCH
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans flex flex-col items-center justify-center px-6 text-center py-10">
      {status === 'loading' && <Loader2 className="w-8 h-8 text-[#00BFFF] animate-spin" />}
      {status === 'error' && (
        <><ShieldAlert className="w-14 h-14 text-gray-700 mb-3" /><p className="text-gray-400">Invalid or expired check-in code.</p></>
      )}
      {(status === 'ready' || status === 'submitting') && info && (
        <>
          <ShieldAlert className="w-12 h-12 text-[#00BFFF] mb-3" />
          <p className="text-xs text-gray-500 uppercase tracking-widest">{info.tournament_name}</p>
          <h1 className="text-2xl font-black mt-1 mb-1">{info.name}</h1>
          <p className="text-sm text-gray-400 mb-8">Confirm your arrival as a match official.</p>
          {err && <p className="text-[#FF3B30] text-sm mb-4">{err}</p>}
          <button data-testid="do-checkin" onClick={checkin} disabled={status === 'submitting'} className="w-full max-w-xs bg-[#00BFFF] text-black font-bold py-4 rounded-2xl disabled:opacity-50">
            {status === 'submitting' ? 'Checking in…' : 'CHECK IN NOW'}
          </button>
        </>
      )}
      {status === 'done' && info && (
        <>
          <CheckCircle2 className="w-14 h-14 text-[#00D26A] mb-3" />
          <h1 className="text-2xl font-black mb-1">Checked In</h1>
          <p className="text-gray-400 text-sm">{info.name} • {info.tournament_name}</p>
          <Fixtures />
        </>
      )}
    </div>
  );
}
